import React, { type FC, useMemo, useEffect } from 'react';
import TraitsConditionalRenderer from '@atlassian/jira-traits/src/TraitsConditionalRenderer.tsx';
import { composeTraitsSelectors } from '@atlassian/jira-traits/src/TraitsStore.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useUserProperty } from '@atlassian/jira-platform-use-user-properties/src/index.tsx';
import pagesSpotlightV4Entrypoint from '../../entrypoint.tsx';

import {
	useIsPagesSpotlightNav4Eligible,
	useIsQuickstartDismissed,
	useIsD0APOrMLAP,
} from '../hooks.tsx';
import { USER_PROPERTY_KEY, type PagesSpotlightMaybeInternalProps } from './constants.tsx';

const entryPointParams = {};

/**
 * Load the Pages Spotlight entry point if the user qualifies for it based on the following conditions:
 * - the user is in the D0AP or MLAP audience
 * - the user has not seen the spotlight before (user property is not set to 'true')
 * - the user has not seen the nav3 spotlight
 */
export const PagesSpotlightEntrypointContainerInternal: FC<PagesSpotlightMaybeInternalProps> = (
	props,
) => {
	const isQuickstartDismissed = useIsQuickstartDismissed();
	const [property] = useUserProperty(USER_PROPERTY_KEY);
	const { isEligible } = useIsPagesSpotlightNav4Eligible();
	const loadEntryPoint = useMemo(() => {
		return isQuickstartDismissed && property !== 'true' && isEligible;
	}, [isQuickstartDismissed, property, isEligible]);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		pagesSpotlightV4Entrypoint,
		entryPointParams,
	);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (loadEntryPoint) {
			entryPointActions.load();
		}

		return () => {
			if (loadEntryPoint) {
				entryPointActions.unload();
			}
		};
	}, [loadEntryPoint, entryPointActions]);

	return (
		<JiraEntryPointContainer
			id="jira-pages-spotlight-nav4"
			packageName="@atlassian/jira-pages-spotlight-nav4"
			entryPointReferenceSubject={entryPointReferenceSubject}
			runtimeProps={props}
		/>
	);
};

/**
 * Wrap PagesSpotlightEntrypointContainerInternal with TraitsConditionalRenderer to ensure it renders
 * only when SITE_USER traits are present and the user is in the D0AP or MLAP audience.
 */
export const PagesSpotlightEntrypointContainer: FC<PagesSpotlightMaybeInternalProps> = (props) => {
	return (
		<TraitsConditionalRenderer
			// eslint-disable-next-line react-hooks/react-compiler
			conditions={composeTraitsSelectors(['SITE_USER'], [useIsD0APOrMLAP])}
		>
			<PagesSpotlightEntrypointContainerInternal {...props} />
		</TraitsConditionalRenderer>
	);
};
